import { Button, Grid, Typography, InputLabel, FormControl, FormHelperText } from '@material-ui/core';
import { Theme, makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import DOMPurify from 'dompurify';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Campo, Formulario } from '../../../interfaces/form-flow';
import { answerForm, getStatusFormFlow } from '../../../services/form-flow';
import { useToastValue } from '../../../contexts/toastMessage';
import getCookie from '../../../utils/getCookies';
import { setCookie } from '../../../utils/setCookie';
import { validateAndReturnResponse, handleApiError } from '../../../utils/apiValidation';
import {
  Area,
  MultCheckboxe,
  Multiple,
  Phone,
  Select,
  Text,
  TextDate,
  TextEmail,
  TextNumber,
} from '../Inputs';
import Arquivo from '../Inputs/Arquivo';
import MultArquivo from '../Inputs/MultArquivo';
import MultipleAPI from '../Inputs/MultipleAPI';
import SelectAPI from '../Inputs/SelectAPI';
import MyEditor from '../../MyEditor';
import PreviewModal from './PreviewModal';
import { useLastResposta } from '../../../contexts/LastRespostaContext';
import {
  isArquivoMetaValido,
  isDataRespostaValida,
  isMultiplosArquivosMetaValido,
  normalizeRespostaCampo,
} from '../../../utils/normalize-resposta-campo';
import {
  listarInstituicoes,
  listarModalidades,
  listarMunicipios,
  OpcaoLabelValue,
} from '../../../services/ensino-webhook';

const TIPOS_SELECT_LABEL_VALUE = [
  'Alternativas_API',
  'Municipio',
  'Instituicao_Ensino',
  'Modalidade',
] as const;

const isTipoSelectLabelValue = (tipo: string): boolean =>
  TIPOS_SELECT_LABEL_VALUE.includes(tipo as typeof TIPOS_SELECT_LABEL_VALUE[number]);

const findCampoByTipo = (forms: Formulario[], tipo: string): Campo | undefined => {
  for (let i = 0; i < forms.length; i += 1) {
    const campo = forms[i].campos.find((item) => item.tipo === tipo);
    if (campo) {
      return campo;
    }
  }
  return undefined;
};

interface Props {
  page: number;
  formData: Formulario[];
  handleStep: (pageOrStep: number) => void;
  handleSubmit: (values: any, errors?: any) => void;
  formsAnswerData?: any[];
  requestMyData: boolean;
  flowPreviewEnabled?: boolean;
  skipAdvanceStepOnLastSave?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: '#264259',
      },
    },
  },
  title: {
    fontFamily: 'Roboto',
    fontWeight: 500,
    color: '#264259',
    textTransform: 'uppercase',
    textAlign: 'center',
    fontSize: 16,
    padding: 20,
  },
  subtitle: {
    opacity: 0.6,
    textAlign: 'center',
    fontWeight: 'normal',
    fontSize: 16,
  },
  formControl: {
    minWidth: 120,
  },
  adornedEnd: {
    paddingRight: 0,
  },
  buttonForm: {
    backgroundColor: '#264259',
  },
  gridContainer: {
    marginTop: 20,
  },
  buttonStyleBase: {
    padding: '5px 10px',
    border: 'none',
    borderRadius: '5px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: '0.3s',
    marginTop: '5px',
    color: '#fff',
  },
}));

const STORAGE_PREFIX = 'form-flow-values';
const COOKIE_PREFIX = 'form-flow-session';
const FILES_STORAGE_PREFIX = 'form-flow-files';

/** Mesma regra de exibição usada no render — validate deve usar isto para não ignorar campos visíveis. */
function computeCampoVisivel(campo: Campo, vals: Record<string, any>): boolean {
  const dependencyFieldId = campo.campo_dependente;

  if (!dependencyFieldId) {
    return true;
  }

  const dependencyValue = vals[dependencyFieldId];

  if (typeof dependencyValue === 'boolean') {
    return dependencyValue === true;
  }
  if (typeof dependencyValue === 'string') {
    const normalized = dependencyValue.trim().toLowerCase();
    if (['true', 'sim', 'yes', '1'].includes(normalized)) return true;
    if (['false', 'não', 'nao', 'no', '0', ''].includes(normalized)) return false;
    return dependencyValue.trim() !== '';
  }
  if (typeof dependencyValue === 'number') {
    return dependencyValue !== 0 && dependencyValue !== null && dependencyValue !== undefined;
  }
  if (typeof dependencyValue === 'object' && dependencyValue !== null) {
    if (Object.prototype.hasOwnProperty.call(dependencyValue, 'value')) {
      const nestedValue = (dependencyValue as { value?: unknown }).value;
      return nestedValue !== null && nestedValue !== undefined && nestedValue !== '';
    }
    return Object.keys(dependencyValue).length > 0;
  }
  return (
    dependencyValue !== null &&
    dependencyValue !== undefined &&
    dependencyValue !== ''
  );
}

const hasWindow = (): boolean => typeof window !== 'undefined';

const getStorageKey = (slug?: string): string | null =>
  slug ? `${STORAGE_PREFIX}-${slug}` : null;

const getCookieKey = (): string => `${COOKIE_PREFIX}-all-values`;

const getFilesStorageKey = (): string => `${FILES_STORAGE_PREFIX}-all-files`;

const readPageValuesFromStorage = (slug?: string): Record<string, any> | null => {
  if (!hasWindow()) return null;
  const key = getStorageKey(slug);
  if (!key) return null;

  try {
    const stored = window.localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Falha ao ler valores salvos do formulário', error);
    return null;
  }
};

const readAllValuesFromCookie = (): Record<string, any> | null => {
  if (!hasWindow()) return null;
  try {
    const cookieData = getCookie(getCookieKey());
    return cookieData ? JSON.parse(cookieData) : null;
  } catch (error) {
    console.warn('Falha ao ler valores do cookie de sessão', error);
    return null;
  }
};

const persistPageValuesToStorage = (
  slug: string | undefined,
  campos: Campo[] = [],
  allValues: Record<string, any> = {},
) => {
  if (!hasWindow()) return;
  const key = getStorageKey(slug);
  if (!key || !campos.length) return;

  const valuesToPersist = campos.reduce<Record<string, any>>((acc, field) => {
    if (allValues[field.id] !== undefined) {
      acc[field.id] = allValues[field.id];
    }
    return acc;
  }, {});

  window.localStorage.setItem(key, JSON.stringify(valuesToPersist));
};

const persistAllValuesToCookie = (allValues: Record<string, any> = {}) => {
  if (!hasWindow()) return;
  try {
    const valuesToSave: Record<string, any> = {};
    const filesToSave: Record<string, any> = {};

    Object.keys(allValues).forEach(key => {
      const value = allValues[key];

      if (value === null || value === undefined) {
        return;
      }

      if (value instanceof File) {
        const fileInfo = {
          name: value.name,
          size: value.size,
          type: value.type,
          lastModified: value.lastModified,
        };
        filesToSave[key] = fileInfo;
        return;
      }

      if (value instanceof FileList) {
        const fileListInfo = Array.from(value).map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        }));
        filesToSave[key] = fileListInfo;
        return;
      }

      if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
        const filesInfo = value.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        }));
        filesToSave[key] = filesInfo;
        return;
      }

      if (typeof value === 'object' && value.constructor === Object) {
        const hasFile = Object.values(value).some(v => v instanceof File);
        if (hasFile) {
          return;
        }
      }

      valuesToSave[key] = value;
    });

    const cookieData = JSON.stringify(valuesToSave);
    setCookie(getCookieKey(), cookieData, 0);

    if (Object.keys(filesToSave).length > 0) {
      try {
        window.localStorage.setItem(getFilesStorageKey(), JSON.stringify(filesToSave));
      } catch (error) {
        console.warn('Falha ao salvar informações de arquivos no localStorage', error);
      }
    }
  } catch (error) {
    console.warn('Falha ao salvar valores no cookie de sessão', error);
  }
};

const readFilesFromStorage = (): Record<string, any> | null => {
  if (!hasWindow()) return null;
  try {
    const stored = window.localStorage.getItem(getFilesStorageKey());
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Falha ao ler informações de arquivos do localStorage', error);
    return null;
  }
};


const clearStoredValuesForForms = (forms: Formulario[] = []) => {
  if (!hasWindow()) return;
  forms.forEach(form => {
    const key = getStorageKey(form.slug);
    if (key) {
      window.localStorage.removeItem(key);
    }
  });
  document.cookie = `${getCookieKey()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  if (hasWindow()) {
    try {
      window.localStorage.removeItem(getFilesStorageKey());
    } catch (error) {
      console.warn('Falha ao limpar informações de arquivos do localStorage', error);
    }
  }
};

export default function Flow({
  page,
  handleStep,
  formData,
  handleSubmit,
  formsAnswerData,
  requestMyData,
  flowPreviewEnabled = false,
  skipAdvanceStepOnLastSave = false,
}: Props): JSX.Element {
  const classes = useStyles();
  const { setToast } = useToastValue();
  const [currentPageData, setCurrentPageData] = useState<Formulario>(
    formData[page],
  );
  const [currentFormsAnswer, setCurrentFormsAnswer] = useState(
    formsAnswerData[page],
  );
  const [values, setValues] = useState<any>({});
  const [valuesPattern, setValuesPattern] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [statusFormFlow, setStatusFormFlow] = useState<any>({});
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const { getResposta, setResposta } = useLastResposta();

  const [replicationCounts, setReplicationCounts] = useState<Record<number, number>>({});
  const [opcoesMunicipio, setOpcoesMunicipio] = useState<OpcaoLabelValue[]>([]);
  const [opcoesInstituicao, setOpcoesInstituicao] = useState<OpcaoLabelValue[]>([]);
  const [opcoesModalidade, setOpcoesModalidade] = useState<OpcaoLabelValue[]>([]);
  const [loadingMunicipio, setLoadingMunicipio] = useState<boolean>(false);
  const [loadingInstituicao, setLoadingInstituicao] = useState<boolean>(false);
  const [loadingModalidade, setLoadingModalidade] = useState<boolean>(false);

  useEffect(() => {
    const initialCounts: Record<number, number> = {};
    formData.forEach(form => {
      form.campos.forEach(campo => {
        if (campo.tipo === 'Matriz') {
          initialCounts[campo.id] = 1;
        }
      });
    });
    setReplicationCounts(initialCounts);
  }, [formData]);

  useEffect(() => {
    const campoMunicipio = findCampoByTipo(formData, 'Municipio');
    if (!campoMunicipio) {
      return undefined;
    }
    let cancelled = false;
    setLoadingMunicipio(true);
    listarMunicipios()
      .then((opcoes) => {
        if (!cancelled) {
          setOpcoesMunicipio(opcoes);
        }
      })
      .catch((err) => {
        console.error('[Flow] Erro ao carregar municípios', err);
        if (!cancelled) {
          setOpcoesMunicipio([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingMunicipio(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formData]);

  const municipioSelecionado = (() => {
    const campo = findCampoByTipo(formData, 'Municipio');
    return campo ? values[campo.id] : null;
  })();
  const instituicaoSelecionada = (() => {
    const campo = findCampoByTipo(formData, 'Instituicao_Ensino');
    return campo ? values[campo.id] : null;
  })();

  useEffect(() => {
    const campoInstituicao = findCampoByTipo(formData, 'Instituicao_Ensino');
    if (!campoInstituicao) {
      return undefined;
    }
    const municipioNome = municipioSelecionado?.label;
    if (!municipioNome) {
      setOpcoesInstituicao([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingInstituicao(true);
    listarInstituicoes(municipioNome)
      .then((opcoes) => {
        if (!cancelled) {
          setOpcoesInstituicao(opcoes);
        }
      })
      .catch((err) => {
        console.error('[Flow] Erro ao carregar instituições', err);
        if (!cancelled) {
          setOpcoesInstituicao([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingInstituicao(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formData, municipioSelecionado?.label]);

  useEffect(() => {
    const campoModalidade = findCampoByTipo(formData, 'Modalidade');
    if (!campoModalidade) {
      return undefined;
    }
    const instituicaoId = instituicaoSelecionada?.value;
    if (instituicaoId === null || instituicaoId === undefined || instituicaoId === '') {
      setOpcoesModalidade([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingModalidade(true);
    listarModalidades(instituicaoId)
      .then((opcoes) => {
        if (!cancelled) {
          setOpcoesModalidade(opcoes);
        }
      })
      .catch((err) => {
        console.error('[Flow] Erro ao carregar modalidades', err);
        if (!cancelled) {
          setOpcoesModalidade([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingModalidade(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formData, instituicaoSelecionada?.value]);

  useEffect(() => {
    const upcomingPageData = formData[page];
    const upcomingAnswerData = formsAnswerData[page];

    if (upcomingAnswerData) {
      setCurrentFormsAnswer(upcomingAnswerData);
    }
    if (upcomingPageData) {
      setCurrentPageData(upcomingPageData);

      if (upcomingPageData.campos.length) {
        const cookieValues = readAllValuesFromCookie();
        const lastRespostaData = getResposta(upcomingPageData.slug);

        setValues(currentValues => {
          const newValues = upcomingPageData.campos.reduce((obj, field) => {
            const fieldId = field.id;
            const respostaServidor =
              upcomingAnswerData?.resposta?.[fieldId]?.resposta;

            if (respostaServidor !== undefined && respostaServidor !== null) {
              obj[fieldId] = respostaServidor;
            } else if (lastRespostaData && lastRespostaData[fieldId]) {
              // Use lastResposta data from context
              const lastRespostaValue = lastRespostaData[fieldId]?.resposta;

              if (field.tipo === 'Arquivo' || field.tipo === 'Multiplos_Arquivos') {
                const fromCurrent = currentValues[fieldId];
                const isMulti = field.tipo === 'Multiplos_Arquivos';
                const isValidCurrent = isMulti
                  ? isMultiplosArquivosMetaValido(fromCurrent)
                  : isArquivoMetaValido(fromCurrent);
                const isValidLast = isMulti
                  ? isMultiplosArquivosMetaValido(lastRespostaValue)
                  : isArquivoMetaValido(lastRespostaValue);
                if (isValidCurrent) {
                  obj[fieldId] = fromCurrent;
                } else if (isValidLast) {
                  obj[fieldId] = lastRespostaValue;
                } else {
                  const filesInfo = readFilesFromStorage();
                  const fromStorage = filesInfo?.[fieldId];
                  const isValidStorage = isMulti
                    ? isMultiplosArquivosMetaValido(fromStorage)
                    : isArquivoMetaValido(fromStorage);
                  obj[fieldId] = isValidStorage
                    ? fromStorage
                    : isMulti
                      ? []
                      : '';
                }
              } else if (field.tipo === 'Alternativas' || isTipoSelectLabelValue(field.tipo)) {
                if (lastRespostaValue !== null && lastRespostaValue !== undefined && lastRespostaValue !== '') {
                  obj[fieldId] = lastRespostaValue;
                } else if (currentValues[fieldId] !== undefined && currentValues[fieldId] !== null && currentValues[fieldId] !== '') {
                  obj[fieldId] = currentValues[fieldId];
                } else {
                  obj[fieldId] = isTipoSelectLabelValue(field.tipo) ? null : '';
                }
              } else {
                if (lastRespostaValue !== null && lastRespostaValue !== undefined) {
                  obj[fieldId] = lastRespostaValue;
                } else if (currentValues[fieldId] !== undefined) {
                  obj[fieldId] = currentValues[fieldId];
                } else {
                  obj[fieldId] = '';
                }
              }
            } else if (cookieValues && cookieValues[fieldId] !== undefined) {
              const cookieValue = cookieValues[fieldId];

              if (field.tipo === 'Arquivo' || field.tipo === 'Multiplos_Arquivos') {
                if (cookieValue !== null && cookieValue !== undefined && cookieValue !== '') {
                  if (typeof cookieValue === 'object' && cookieValue.url && cookieValue.nome && cookieValue.id) {
                    obj[fieldId] = cookieValue;
                  } else if (Array.isArray(cookieValue) && cookieValue.length > 0 && cookieValue[0]?.url && cookieValue[0]?.nome && cookieValue[0]?.id) {
                    obj[fieldId] = cookieValue;
                  } else if (currentValues[fieldId] !== undefined && currentValues[fieldId] !== null && currentValues[fieldId] !== '') {
                    obj[fieldId] = currentValues[fieldId];
                  } else {
                    obj[fieldId] = field.tipo === 'Multiplos_Arquivos' ? [] : '';
                  }
                } else if (currentValues[fieldId] !== undefined && currentValues[fieldId] !== null && currentValues[fieldId] !== '') {
                  obj[fieldId] = currentValues[fieldId];
                } else {
                  obj[fieldId] = field.tipo === 'Multiplos_Arquivos' ? [] : '';
                }
              } else if (field.tipo === 'Alternativas' || isTipoSelectLabelValue(field.tipo)) {
                if (cookieValue !== '' && cookieValue !== null && cookieValue !== undefined) {
                  obj[fieldId] = cookieValue;
                } else if (currentValues[fieldId] !== undefined && currentValues[fieldId] !== null && currentValues[fieldId] !== '') {
                  obj[fieldId] = currentValues[fieldId];
                } else {
                  obj[fieldId] = isTipoSelectLabelValue(field.tipo) ? null : '';
                }
              } else {
                if (cookieValue !== null && cookieValue !== undefined) {
                  obj[fieldId] = cookieValue;
                } else if (currentValues[fieldId] !== undefined) {
                  obj[fieldId] = currentValues[fieldId];
                } else {
                  obj[fieldId] = '';
                }
              }
            } else if (currentValues[fieldId] !== undefined) {
              obj[fieldId] = currentValues[fieldId];
            } else {
              obj[fieldId] = field.tipo === 'Multiplos_Arquivos' ? [] : '';
            }
            return obj;
          }, {});

          return Object.assign({}, currentValues, newValues);
        });

        setErrors(currentValues => {
          const newValues = upcomingPageData.campos.reduce((obj, field) => {
            obj[field.id] = '';
            return obj;
          }, {});
          return Object.assign({}, newValues, currentValues);
        });
      }
    }
  }, [page, formData, formsAnswerData]);

  useEffect(() => {
    if (!currentPageData?.slug) return;
    if (currentFormsAnswer?.formulario === currentPageData.slug) return;

    const storedValues = readPageValuesFromStorage(currentPageData.slug);
    const cookieValues = readAllValuesFromCookie();

    const valuesToRestore = cookieValues || storedValues;

    if (valuesToRestore) {
      setValues(currentValues => {
        const restored = { ...currentValues };

        Object.keys(valuesToRestore).forEach(key => {
          const value = valuesToRestore[key];
          const field = currentPageData.campos.find(c => c.id.toString() === key.toString());

          if (field) {
            if (field.tipo === 'Arquivo' || field.tipo === 'Multiplos_Arquivos') {
              if (value !== null && value !== undefined && value !== '') {
                if (typeof value === 'object' && value.url && value.nome && value.id) {
                  restored[key] = value;
                } else if (Array.isArray(value) && value.length > 0 && value[0]?.url && value[0]?.nome && value[0]?.id) {
                  restored[key] = value;
                } else if (currentValues[key] !== undefined && currentValues[key] !== null && currentValues[key] !== '') {
                  restored[key] = currentValues[key];
                } else {
                  restored[key] = field.tipo === 'Multiplos_Arquivos' ? [] : '';
                }
              } else if (currentValues[key] !== undefined && currentValues[key] !== null && currentValues[key] !== '') {
                restored[key] = currentValues[key];
              } else {
                restored[key] = field.tipo === 'Multiplos_Arquivos' ? [] : '';
              }
            } else if (field.tipo === 'Alternativas' || isTipoSelectLabelValue(field.tipo)) {
              if (value !== '' && value !== null && value !== undefined) {
                restored[key] = value;
              } else if (currentValues[key] !== undefined && currentValues[key] !== null && currentValues[key] !== '') {
                restored[key] = currentValues[key];
              }
            } else {
              if (value !== null && value !== undefined) {
                restored[key] = value;
              }
            }
          } else {
            restored[key] = value;
          }
        });

        return restored;
      });
    }
  }, [currentPageData?.slug, currentFormsAnswer?.formulario]);

  useEffect(() => {
    if (!currentPageData?.slug || !currentPageData.campos?.length) return;
    persistPageValuesToStorage(
      currentPageData.slug,
      currentPageData.campos,
      values,
    );
    persistAllValuesToCookie(values);
  }, [values, currentPageData]);

  const handleSave = async slug => {
    const formDataUser = JSON.parse(getCookie('gov_user_form_started') || '{}');
    const user = JSON.parse(getCookie('gov_user_data') || '{}');

    if (!formDataUser || !formDataUser.id) {
      console.error('[ERROR] flow_resposta não encontrado no cookie gov_user_form_started');
      alert('Erro: Não foi possível encontrar o identificador da solicitação. Por favor, recarregue a página.');
      return;
    }

    if (formDataUser) {
      const forResposta = {};
      currentPageData.campos.forEach(field => {
        const fieldId = field.id;
        const value = normalizeRespostaCampo(field, values[fieldId]);
        forResposta[fieldId] = {
          id: fieldId,
          resposta: value,
        };
      });

      setResposta(slug, forResposta);

      const formatData = {
        flow_resposta: formDataUser.id,
        formulario: slug,
        cpf: user.cpf,
        resposta: forResposta,
      };

      try {
        const response = await answerForm(formatData);

        if (response) {
          const nextPage = page + 1;
          const isLastPage = page >= formData.length - 1;
          if (!isLastPage || !skipAdvanceStepOnLastSave) {
            handleStep(nextPage);
          }
        }
      } catch (error) {
        const errorData = (error as any)?.response?.data;
        const hasFileErrors = errorData?.erros_arquivos && typeof errorData.erros_arquivos === 'object';
        const errorMessage = errorData?.message || errorData?.error;
        const isFileError =
          hasFileErrors ||
          errorMessage?.toLowerCase().includes('arquivo') ||
          errorMessage?.toLowerCase().includes('file') ||
          errorMessage?.toLowerCase().includes('tipo') ||
          errorMessage?.toLowerCase().includes('formato') ||
          errorMessage?.toLowerCase().includes('extensão') ||
          (error as any)?.response?.status === 400 ||
          (error as any)?.response?.status === 406 ||
          (error as any)?.response?.status === 415;

        const finalErrorMessage = handleApiError(error as any, isFileError);

        setToast({
          visible: true,
          type: 'error',
          message: isFileError
            ? `Erro ao salvar formulário: ${finalErrorMessage}. Verifique os arquivos anexados.`
            : `Erro ao salvar formulário: ${finalErrorMessage}`,
        });

        throw error;
      }
    }
  };

  const onSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (validate()) {
      try {
        const totalPage = formData.length - 1;

        if (page <= totalPage) {
          await handleSave(currentPageData.slug);
        }

        if (page === totalPage) {
          await checkStatusFormFlow();
          handleSubmit(values, errors);
          clearStoredValuesForForms(formData);
        }
      } catch (error) {
        // Erro já foi tratado no handleSave, apenas impede continuação
        return;
      }
    }
  };

  const checkStatusFormFlow = async () => {
    const formDataUser = JSON.parse(getCookie('gov_user_form_started') || '{}');
    const user = JSON.parse(getCookie('gov_user_data') || '{}');
    if (formDataUser?.identificador && user?.cpf) {
      try {
        const response = await getStatusFormFlow({
          identificador: formDataUser.identificador,
          cpf: user.cpf,
        });

        validateAndReturnResponse(response);
        setStatusFormFlow(response.data);
      } catch (error) {
        const errorMessage = handleApiError(error as any);

        setToast({
          visible: true,
          type: 'error',
          message: errorMessage,
        });

        throw error;
      }
    }
  };

  // Valida campos, para tratamento externo
  const validate = useCallback(() => {
    const invalids: unknown[] = [];
    const currentValues: Record<string, any> = {};

    const isRequiredAnswerMissing = (val: any, field: Campo): boolean => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'number') return Number.isNaN(val);
      if (typeof val === 'boolean') return false;
      if (typeof val === 'string') return val.trim() === '';
      if (val instanceof Date) return isNaN(val.getTime());
      if (Array.isArray(val)) {
        if (field.tipo === 'Multiplos_Arquivos' || field.tipo === 'Multiplos' || field.tipo === 'Multiplos_API') {
          return val.length === 0;
        }
        return val.length === 0;
      }
      if (typeof val === 'object') {
        if (isTipoSelectLabelValue(field.tipo)) {
          return val.value === null || val.value === undefined || val.value === '';
        }
        if (field.tipo === 'Arquivo') {
          if (val instanceof File) return false;
          if (val.url && val.nome) return false;
          return true;
        }
        if (field.tipo === 'Multiplos_Arquivos') {
          return !Array.isArray(val) || val.length === 0;
        }
        if (Object.keys(val).length === 0) {
          return true;
        }
        return false;
      }
      return false;
    };

    const getMatrixCellValue = (campoId: number, ordem: string | number, rowIdx: number): any => {
      const row = values[campoId]?.[rowIdx];
      if (!row || !Array.isArray(row)) return null;
      const match = row.find(
        (item: { id: string | number; resposta?: any }) => String(item.id) === String(ordem),
      );
      return match?.resposta ?? null;
    };

    const isMatrixCellMissing = (subTipo: string, cellVal: any): boolean => {
      if (cellVal === null || cellVal === undefined) return true;
      if (typeof cellVal === 'number') return Number.isNaN(cellVal);
      if (typeof cellVal === 'boolean') return false;
      if (typeof cellVal === 'string') return cellVal.trim() === '';
      if (cellVal instanceof Date) return isNaN(cellVal.getTime());
      if (Array.isArray(cellVal)) return cellVal.length === 0;
      return false;
    };

    if (currentPageData?.campos?.length) {
      currentPageData.campos.forEach(field => {
        const fieldId = field.id;
        const value = values[fieldId];
        const isVisible = computeCampoVisivel(field, values);

        if (field.tipo === 'Matriz') {
          if (field.obrigatorio && isVisible) {
            const rows = Math.max(1, Number(replicationCounts[fieldId]) || 1);
            const perAltErrors = field.alternativas.map(() => '');
            let matrixInvalid = false;

            for (let rowIdx = 0; rowIdx < rows; rowIdx++) {
              field.alternativas.forEach((alternativa, altIndex) => {
                const cell = getMatrixCellValue(fieldId, alternativa.ordem, rowIdx);

                if (alternativa.campo === 'Data' && cell) {
                  const asStr = typeof cell === 'string' ? cell.trim() : '';
                  if (asStr && !/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) {
                    perAltErrors[altIndex] = 'Data incompleta ou inválida';
                    matrixInvalid = true;
                    return;
                  }
                }

                if (isMatrixCellMissing(alternativa.campo, cell)) {
                  perAltErrors[altIndex] = 'Campo obrigatório';
                  matrixInvalid = true;
                }
              });
            }

            if (matrixInvalid) {
              currentValues[fieldId] = perAltErrors;
            }
          }
        } else if (field.obrigatorio && isVisible && isRequiredAnswerMissing(value, field)) {
          currentValues[fieldId] = 'Campo obrigatório';
        }

        if (field.tipo === 'Data' && value) {
          if (!isDataRespostaValida(value)) {
            currentValues[fieldId] = 'Data incompleta ou inválida';
          }
        }

        const hasValueForValidator =
          value !== null &&
          value !== undefined &&
          value !== '' &&
          !(typeof value === 'number' && Number.isNaN(value));

        if (field.validador && hasValueForValidator && field.tipo !== 'Matriz') {
          const asStr = typeof value === 'string' ? value : String(value);
          const reg = new RegExp(field.validador, 'g').test(asStr);
          if (!reg) {
            currentValues[fieldId] = 'Campo no formato inválido';
          }
        }

        if (currentValues[fieldId]) {
          invalids.push(currentValues[fieldId]);
        }
      });
    }

    setErrors(currentValues);
    return !invalids.length;
  }, [currentPageData, values, replicationCounts]);

  const sanitizeHTML = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'table', 'thead', 'tbody',
        'tr', 'th', 'td', 'span', 'div', 'pre', 'code', 's', 'sub', 'sup'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel', 'src', 'alt', 'title', 'width', 'height',
        'class', 'id', 'style', 'colspan', 'rowspan', 'align'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      KEEP_CONTENT: true,
      RETURN_TRUSTED_TYPE: false,
    });
  };

  const fieldChanged = (fieldId: number, value: any, fieldType?: string) => {
    let sanitizedValue = value;
    if (fieldType === 'Area' && typeof value === 'string') {
      sanitizedValue = sanitizeHTML(value);
    }
    const tipoAlterado = formData
      .flatMap((form) => form.campos)
      .find((campo) => campo.id === fieldId)?.tipo;
    const campoInstituicao = findCampoByTipo(formData, 'Instituicao_Ensino');
    const campoModalidade = findCampoByTipo(formData, 'Modalidade');

    setValues(currentValues => {
      const updatedValues = {
        ...currentValues,
        [fieldId]: sanitizedValue,
      };
      if (tipoAlterado === 'Municipio') {
        if (campoInstituicao) {
          updatedValues[campoInstituicao.id] = null;
        }
        if (campoModalidade) {
          updatedValues[campoModalidade.id] = null;
        }
        setOpcoesInstituicao([]);
        setOpcoesModalidade([]);
      }
      if (tipoAlterado === 'Instituicao_Ensino') {
        if (campoModalidade) {
          updatedValues[campoModalidade.id] = null;
        }
        setOpcoesModalidade([]);
      }
      return updatedValues;
    });
    setErrors(currentError => {
      const updatedErrors = { ...currentError };
      updatedErrors[fieldId] = '';
      if (tipoAlterado === 'Municipio') {
        if (campoInstituicao) {
          updatedErrors[campoInstituicao.id] = '';
        }
        if (campoModalidade) {
          updatedErrors[campoModalidade.id] = '';
        }
      }
      if (tipoAlterado === 'Instituicao_Ensino' && campoModalidade) {
        updatedErrors[campoModalidade.id] = '';
      }
      return updatedErrors;
    });
  };

  const handlePrev = () => {
    const pagePrev = page - 1;
    if (pagePrev >= 0) {
      // Persist current page data before going back
      if (currentPageData?.slug) {
        persistPageValuesToStorage(
          currentPageData.slug,
          currentPageData.campos,
          values,
        );
        persistAllValuesToCookie(values);
      }
      handleStep(pagePrev);
    }
  };

  const hasPageNext = () => {
    return (
      (page >= 0 && page < formData.length - 1) || formData.length - 1 === page
    );
  };

  const getValueFromUser = (titulo: string, fieldId: number) => {
    if (!requestMyData) return '';
    const user = JSON.parse(getCookie('gov_user_data') || '{}');
    switch (titulo) {
      case 'CPF':
      case 'cpf':
      case 'Cpf':
        setValues(currentValues => {
          return {
            ...currentValues,
            [fieldId]: user.cpf,
          };
        });
        setValuesPattern(currentValues => {
          return {
            ...currentValues,
            [fieldId]: user.cpf,
          };
        });
        break;
      case 'NOME':
      case 'Nome':
      case 'nome':
        setValues(currentValues => {
          return {
            ...currentValues,
            [fieldId]: user.nome,
          };
        });
        setValuesPattern(currentValues => {
          return {
            ...currentValues,
            [fieldId]: user.nome,
          };
        });
        break;
      default:
        return '';
    }
  };

  const getEmailFromUser = (fieldId: number) => {
    if (!requestMyData) return '';
    const user = JSON.parse(getCookie('gov_user_data') || '{}');

    if (user?.contato?.email) {
      setValues(currentValues => {
        return {
          ...currentValues,
          [fieldId]: user.contato.email,
        };
      });
      setValuesPattern(currentValues => {
        return {
          ...currentValues,
          [fieldId]: user.contato.email,
        };
      });
    }
  };

  const getTelefoneFromUser = (fieldId: number) => {
    if (!requestMyData) return '';
    const user = JSON.parse(getCookie('gov_user_data') || '{}');

    if (user?.contato?.celular) {
      setValues(currentValues => {
        return {
          ...currentValues,
          [fieldId]: user.contato.celular,
        };
      });
      setValuesPattern(currentValues => {
        return {
          ...currentValues,
          [fieldId]: user.contato.celular,
        };
      });
    }
  };

  const matrixFieldChanged = (campoId, ordem, value, label, idx, fieldType?: string) => {
    let sanitizedValue = value;
    if (fieldType === 'Area' && typeof value === 'string') {
      sanitizedValue = sanitizeHTML(value);
    }

    setValues(prevValues => {
      const updatedValues = { ...prevValues };

      if (!updatedValues[campoId]) {
        updatedValues[campoId] = [];
      }

      if (!updatedValues[campoId][idx]) {
        updatedValues[campoId][idx] = [];
      }

      const indexExistingObject = updatedValues[campoId][idx]?.findIndex(
        obj => obj.id === ordem,
      );

      if (indexExistingObject > -1) {
        updatedValues[campoId][idx][indexExistingObject].resposta = sanitizedValue;
      } else {
        updatedValues[campoId][idx].push({
          id: ordem,
          resposta: sanitizedValue,
          label: label,
        });
      }

      return updatedValues;
    });
  };

  const findValueForInput = (campoId, ordem, idx) => {
    const respostaArray = values[campoId]?.[idx];

    if (!respostaArray) return null;

    const obj = respostaArray.find(item => item.id === ordem);

    return obj?.resposta || null;
  };

  function convertDateFormat(dateString) {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  function convertToLongFormat(dateString: string | null | undefined) {
    if (!dateString) {
      return '';
    }

    const [day, month, year] = dateString.split('/').map(Number);

    const date = new Date(year, month - 1, day, 11, 11);

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      timeZone: 'America/Sao_Paulo',
    };
    const longDate =
      date.toLocaleString('en-US', options) + ' (Horário Padrão de Brasília)';

    return longDate;
  }

  const handleReplicate = (campoId, limite) => {
    setReplicationCounts(prevCounts => {
      const newCount =
        prevCounts[campoId] < limite + 1
          ? prevCounts[campoId] + 1
          : prevCounts[campoId];
      return { ...prevCounts, [campoId]: newCount };
    });
  };

  const handleDeleteField = campoId => {
    setReplicationCounts(prevCounts => {
      const newCount = prevCounts[campoId] > 1 ? prevCounts[campoId] - 1 : 1;
      return { ...prevCounts, [campoId]: newCount };
    });
  };

  const renderActionButton = (campoId, idx, limite) => {
    if (limite === 0) return null;

    if (idx === 0 && replicationCounts[campoId] < limite + 1) {
      return (
        <>
          <button
            type='button'
            onClick={() => handleReplicate(campoId, limite)}
            className={classes.buttonStyleBase}
            style={{ backgroundColor: '#007BFF' }}
          >
            Duplicar
          </button>
        </>
      );
    } else if (idx > 0) {
      return (
        <>
          <button
            type='button'
            onClick={() => handleDeleteField(campoId)}
            className={classes.buttonStyleBase}
            style={{ backgroundColor: 'red' }}
          >
            Excluir
          </button>
        </>
      );
    }
    return null;
  };

  const shouldShowField = (campo: Campo): boolean => computeCampoVisivel(campo, values);

  return (
    <>
      {currentPageData && (
        <>
          <Grid container alignItems='center' justify='center'>
            <Grid item xs={12}>
              <Typography variant='h6' className={classes.title}>
                {currentPageData.titulo}
              </Typography>
            </Grid>
            <Grid
              style={{ textAlign: 'center', marginBottom: 20 }}
              item
              xs={12}
            >
              <Typography className={classes.subtitle}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(currentPageData.descricao),
                  }}
                />
              </Typography>
            </Grid>
          </Grid>

          <form onSubmit={onSubmit} className={classes.root} noValidate>
            {!currentPageData.campos.length ? (
              <Alert severity='warning' style={{ marginBottom: 10 }}>
                Nenhum campo disponível
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {currentPageData.campos.map((campo: Campo) => (
                  <Fragment key={campo.id}>
                    {shouldShowField(campo) && (
                      <Fragment>
                        {campo.tipo === 'Texto' && (
                          <Grid sm={12} md={6} item>
                            <Text
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                values[campo.id]
                                  ? values[campo.id]
                                  : getValueFromUser(campo.titulo, campo.id)
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={e => fieldChanged(campo.id, e.target.value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                              mask={
                                campo.mascara !== 'mascara' ? campo.mascara : ''
                              }
                            />
                          </Grid>
                        )}
                        <Grid xs={12} md={12} />
                        {Array.from({ length: replicationCounts[campo.id] }).map(
                          (_, idx) => (
                            <>
                              {campo.tipo === 'Matriz' &&
                                campo.alternativas.map((alternativa, index) => {
                                  const dateValue = findValueForInput(
                                    campo.id,
                                    alternativa.ordem,
                                    idx,
                                  );
                                  switch (alternativa.campo) {
                                    case 'Data':
                                      return (
                                        <Grid
                                          xs={12}
                                          md={6}
                                          item
                                          key={`${campo.id}-${index}-${idx}`}
                                        >
                                          <TextDate
                                            required={campo.obrigatorio}
                                            id={`${alternativa.campo}-${campo.id}-${index}-${idx}`}
                                            label={alternativa.titulo}
                                            value={
                                              dateValue
                                                ? convertToLongFormat(dateValue)
                                                : null
                                            }
                                            error={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? true
                                                : false
                                            }
                                            onChange={dateEvent => {
                                              matrixFieldChanged(
                                                campo.id,
                                                alternativa.ordem,
                                                convertDateFormat(dateEvent),
                                                alternativa.titulo,
                                                idx,
                                              );
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                            helperText={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? errors[campo.id][index]
                                                : alternativa.texto_ajuda
                                            }
                                            disabled={
                                              currentFormsAnswer?.formulario ===
                                              currentPageData.slug ||
                                              !!Object.keys(valuesPattern).find(
                                                chave =>
                                                  valuesPattern[chave] ===
                                                  values[campo.id],
                                              )
                                            }
                                          />
                                        </Grid>
                                      );
                                    case 'Texto':
                                      return (
                                        <Grid
                                          xs={12}
                                          md={6}
                                          item
                                          key={`${campo.id}-${index}-${idx}`}
                                        >
                                          <Text
                                            required={campo.obrigatorio}
                                            id={`${alternativa.campo}-${campo.id}-${index}-${idx}`}
                                            label={alternativa.titulo}
                                            value={findValueForInput(
                                              campo.id,
                                              alternativa.ordem,
                                              idx,
                                            )}
                                            error={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? true
                                                : false
                                            }
                                            onChange={data => {
                                              matrixFieldChanged(
                                                campo.id,
                                                alternativa.ordem,
                                                data.target.value,
                                                alternativa.titulo,
                                                idx,
                                              );
                                            }}
                                            mask={campo.mascara}
                                            helperText={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? errors[campo.id][index]
                                                : alternativa.texto_ajuda
                                            }
                                            disabled={
                                              currentFormsAnswer?.formulario ===
                                              currentPageData.slug ||
                                              !!Object.keys(valuesPattern).find(
                                                chave =>
                                                  valuesPattern[chave] ===
                                                  values[campo.id],
                                              )
                                            }
                                          />
                                        </Grid>
                                      );
                                    case 'Telefone':
                                      return (
                                        <Grid
                                          xs={12}
                                          md={6}
                                          item
                                          key={`${campo.id}-${index}-${idx}`}
                                        >
                                          <Phone
                                            required={campo.obrigatorio}
                                            id={`${alternativa.campo}-${campo.id}-${index}-${idx}`}
                                            label={alternativa.titulo}
                                            value={findValueForInput(
                                              campo.id,
                                              alternativa.ordem,
                                              idx,
                                            )}
                                            error={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? true
                                                : false
                                            }
                                            onChange={event => {
                                              matrixFieldChanged(
                                                campo.id,
                                                alternativa.ordem,
                                                event.target.value,
                                                alternativa.titulo,
                                                idx,
                                              );
                                            }}
                                            mask={campo.mascara}
                                            helperText={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? errors[campo.id][index]
                                                : alternativa.texto_ajuda
                                            }
                                            disabled={
                                              currentFormsAnswer?.formulario ===
                                              currentPageData.slug ||
                                              !!Object.keys(valuesPattern).find(
                                                chave =>
                                                  valuesPattern[chave] ===
                                                  values[campo.id],
                                              )
                                            }
                                          />
                                        </Grid>
                                      );
                                    case 'Email':
                                      return (
                                        <Grid
                                          xs={12}
                                          md={6}
                                          item
                                          key={`${campo.id}-${index}-${idx}`}
                                        >
                                          <TextEmail
                                            required={campo.obrigatorio}
                                            id={`${alternativa.campo}-${campo.id}-${index}-${idx}`}
                                            label={alternativa.titulo}
                                            value={findValueForInput(
                                              campo.id,
                                              alternativa.ordem,
                                              idx,
                                            )}
                                            error={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? true
                                                : false
                                            }
                                            onChange={e =>
                                              matrixFieldChanged(
                                                campo.id,
                                                alternativa.ordem,
                                                e.target.value,
                                                alternativa.titulo,
                                                idx,
                                              )
                                            }
                                            helperText={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? errors[campo.id][index]
                                                : alternativa.texto_ajuda
                                            }
                                            disabled={
                                              currentFormsAnswer?.formulario ===
                                              currentPageData.slug ||
                                              !!Object.keys(valuesPattern).find(
                                                chave =>
                                                  valuesPattern[chave] ===
                                                  values[campo.id],
                                              )
                                            }
                                          />
                                        </Grid>
                                      );
                                    case 'Area':
                                      return (
                                        <Grid
                                          xs={12}
                                          md={6}
                                          item
                                          key={`${campo.id}-${index}-${idx}`}
                                        >
                                          <Area
                                            required={campo.obrigatorio}
                                            id={`${alternativa.campo}-${campo.id}-${index}-${idx}`}
                                            label={alternativa.titulo}
                                            value={findValueForInput(
                                              campo.id,
                                              alternativa.ordem,
                                              idx,
                                            )}
                                            error={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? true
                                                : false
                                            }
                                            onChange={data =>
                                              matrixFieldChanged(
                                                campo.id,
                                                alternativa.ordem,
                                                data.target.value,
                                                alternativa.titulo,
                                                idx,
                                                'Area',
                                              )
                                            }
                                            helperText={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? errors[campo.id][index]
                                                : alternativa.texto_ajuda
                                            }
                                            disabled={
                                              currentFormsAnswer?.formulario ===
                                              currentPageData.slug ||
                                              !!Object.keys(valuesPattern).find(
                                                chave =>
                                                  valuesPattern[chave] ===
                                                  values[campo.id],
                                              )
                                            }
                                          />
                                        </Grid>
                                      );
                                    case 'Numero':
                                      return (
                                        <Grid
                                          xs={12}
                                          md={6}
                                          item
                                          key={`${campo.id}-${index}-${idx}`}
                                        >
                                          <TextNumber
                                            id={`${alternativa.campo}-${campo.id}-${index}-${idx}`}
                                            label={alternativa.titulo}
                                            value={findValueForInput(
                                              campo.id,
                                              alternativa.ordem,
                                              idx,
                                            )}
                                            required={campo.obrigatorio}
                                            onChange={data =>
                                              matrixFieldChanged(
                                                campo.id,
                                                alternativa.ordem,
                                                data.target.value,
                                                alternativa.titulo,
                                                idx,
                                              )
                                            }
                                            helperText={
                                              errors[campo.id] &&
                                                errors[campo.id][index]
                                                ? errors[campo.id][index]
                                                : alternativa.texto_ajuda
                                            }
                                            disabled={
                                              currentFormsAnswer?.formulario ===
                                              currentPageData.slug ||
                                              !!Object.keys(valuesPattern).find(
                                                chave =>
                                                  valuesPattern[chave] ===
                                                  values[campo.id],
                                              )
                                            }
                                          />
                                        </Grid>
                                      );
                                    default:
                                      return null;
                                  }
                                })}
                              {campo.tipo === 'Matriz' && (
                                <Grid
                                  item
                                  xs={12}
                                  md={12}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  {renderActionButton(
                                    campo.id,
                                    idx,
                                    campo.limite_replicacoes,
                                  )}
                                </Grid>
                              )}
                            </>
                          ),
                        )}
                        {campo.tipo === 'Area' && (
                          <Grid sm={12} md={12} item>
                            {campo.titulo && (
                              <Typography
                                variant="subtitle2"
                                style={{ marginBottom: 8, fontWeight: 500, color: '#264259' }}
                              >
                                {campo.titulo}
                                {campo.obrigatorio && <span style={{ color: 'red' }}> *</span>}
                              </Typography>
                            )}
                            <MyEditor
                              id={`${campo.tipo}-${campo.id}`}
                              value={values[campo.id] ? values[campo.id] : ''}
                              setValue={(content: string) => fieldChanged(campo.id, content, 'Area')}
                              disabled={false}
                            />
                            {(errors[campo.id] || campo.texto_ajuda) && (
                              <Typography
                                variant="caption"
                                color={errors[campo.id] ? "error" : "textSecondary"}
                                style={{ marginTop: 4, display: 'block' }}
                              >
                                {errors[campo.id] || campo.texto_ajuda}
                              </Typography>
                            )}
                          </Grid>
                        )}
                        {campo.tipo === 'Email' && (
                          <Grid sm={12} md={4} item>
                            <TextEmail
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                values[campo.id]
                                  ? values[campo.id]
                                  : getEmailFromUser(campo.id)
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={e => fieldChanged(campo.id, e.target.value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Numero' && (
                          <Grid sm={12} md={6} item>
                            <TextNumber
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={values[campo.id] ? values[campo.id] : ''}
                              required={campo.obrigatorio}
                              onChange={e => fieldChanged(campo.id, e.target.value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Telefone' && (
                          <Grid sm={12} md={3} item>
                            <Phone
                              required={campo.obrigatorio ? true : false}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                values[campo.id]
                                  ? values[campo.id]
                                  : getTelefoneFromUser(campo.id)
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={e => fieldChanged(campo.id, e.target.value)}
                              mask={campo.mascara}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Data' && (
                          <Grid sm={12} md={3} item>
                            <TextDate
                              required={campo.obrigatorio}
                              label={campo.titulo}
                              value={values[campo.id] ? values[campo.id] : null}
                              error={errors[campo.id] ? true : false}
                              onChange={data => fieldChanged(campo.id, data)}
                              InputLabelProps={{ shrink: true }}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Alternativas' &&
                          campo.tipo_alternativa === 'RadioButton' && (
                            <Grid sm={12} md={12} item>
                              <MultCheckboxe
                                required={campo.obrigatorio}
                                id={`${campo.tipo}-${campo.id}`}
                                label={campo.titulo}
                                value={values[campo.id] ? values[campo.id] : ''}
                                error={errors[campo.id] ? true : false}
                                onChange={e =>
                                  fieldChanged(campo.id, e.target.value)
                                }
                                helperText={
                                  errors[campo.id]
                                    ? errors[campo.id]
                                    : campo.texto_ajuda
                                }
                                itens={campo.alternativas}
                                disabled={
                                  currentFormsAnswer?.formulario ===
                                  currentPageData.slug ||
                                  !!Object.keys(valuesPattern).find(
                                    chave =>
                                      valuesPattern[chave] === values[campo.id],
                                  )
                                }
                              />
                            </Grid>
                          )}
                        {campo.tipo === 'Alternativas' &&
                          campo.tipo_alternativa === 'Select' && (
                            <Grid sm={12} md={6} item>
                              <Select
                                required={campo.obrigatorio}
                                id={`${campo.tipo}-${campo.id}`}
                                label={campo.titulo}
                                value={
                                  typeof values[campo.id] === 'object' &&
                                  values[campo.id] !== null
                                    ? values[campo.id].value ??
                                      values[campo.id].valor ??
                                      ''
                                    : values[campo.id]
                                      ? values[campo.id]
                                      : ''
                                }
                                error={errors[campo.id] ? true : false}
                                onChange={e =>
                                  fieldChanged(campo.id, e.target.value)
                                }
                                helperText={
                                  errors[campo.id]
                                    ? errors[campo.id]
                                    : campo.texto_ajuda
                                }
                                itens={campo.alternativas}
                                getOptionLabel={option => option.titulo}
                                disabled={
                                  currentFormsAnswer?.formulario ===
                                  currentPageData.slug ||
                                  !!Object.keys(valuesPattern).find(
                                    chave =>
                                      valuesPattern[chave] === values[campo.id],
                                  )
                                }
                              />
                            </Grid>
                          )}
                        {campo.tipo === 'Alternativas_API' && (
                          <Grid sm={12} md={6} item>
                            <SelectAPI
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                typeof values[campo.id] === 'object' &&
                                values[campo.id] !== null
                                  ? values[campo.id].value ??
                                    values[campo.id].valor ??
                                    values[campo.id]
                                  : values[campo.id] !== undefined &&
                                      values[campo.id] !== null &&
                                      values[campo.id] !== ''
                                    ? values[campo.id]
                                    : null
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              itens={campo.alternativas}
                              getOptionLabel={option => option.label}
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Municipio' && (
                          <Grid sm={12} md={6} item>
                            <SelectAPI
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                typeof values[campo.id] === 'object' &&
                                values[campo.id] !== null
                                  ? values[campo.id].value ?? null
                                  : null
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              options={opcoesMunicipio}
                              loading={loadingMunicipio}
                              getOptionLabel={option => option.label}
                              disabled={
                                loadingMunicipio ||
                                currentFormsAnswer?.formulario ===
                                  currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Instituicao_Ensino' && (
                          <Grid sm={12} md={6} item>
                            <SelectAPI
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                typeof values[campo.id] === 'object' &&
                                values[campo.id] !== null
                                  ? values[campo.id].value ?? null
                                  : null
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              options={opcoesInstituicao}
                              loading={loadingInstituicao}
                              getOptionLabel={option => option.label}
                              disabled={
                                loadingInstituicao ||
                                currentFormsAnswer?.formulario ===
                                  currentPageData.slug ||
                                !municipioSelecionado?.label ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Modalidade' && (
                          <Grid sm={12} md={6} item>
                            <SelectAPI
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={
                                typeof values[campo.id] === 'object' &&
                                values[campo.id] !== null
                                  ? values[campo.id].value ?? null
                                  : null
                              }
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              options={opcoesModalidade}
                              loading={loadingModalidade}
                              getOptionLabel={option => option.label}
                              disabled={
                                loadingModalidade ||
                                currentFormsAnswer?.formulario ===
                                  currentPageData.slug ||
                                instituicaoSelecionada?.value === null ||
                                instituicaoSelecionada?.value === undefined ||
                                instituicaoSelecionada?.value === '' ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Multiplos' && (
                          <Grid sm={12} md={12} item>
                            <Multiple
                              required={campo.obrigatorio}
                              label={campo.titulo}
                              values={values[campo.id] ? values[campo.id] : []}
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              InputLabelProps={{ shrink: true }}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              itens={campo.alternativas}
                              getOptionLabel={option => option.label}
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Multiplos_API' && (
                          <Grid sm={12} md={6} item>
                            <MultipleAPI
                              required={campo.obrigatorio}
                              label={campo.titulo}
                              values={values[campo.id] ? values[campo.id] : []}
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              InputLabelProps={{ shrink: true }}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              itens={campo.alternativas}
                              getOptionLabel={option => option.label}
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                            />
                          </Grid>
                        )}

                        {campo.tipo === 'Arquivo' && (
                          <Grid sm={12} md={6} item>
                            <Arquivo
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={values[campo.id] ? values[campo.id] : ''}
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                              tipos_arquivos={campo.tipos_arquivos}
                              tamanho_maximo_permitido={campo.tamanho_maximo_permitido}
                            />
                          </Grid>
                        )}
                        {campo.tipo === 'Multiplos_Arquivos' && (
                          <Grid sm={12} md={12} item>
                            <MultArquivo
                              required={campo.obrigatorio}
                              id={`${campo.tipo}-${campo.id}`}
                              label={campo.titulo}
                              value={values[campo.id] ? values[campo.id] : []}
                              error={errors[campo.id] ? true : false}
                              onChange={value => fieldChanged(campo.id, value)}
                              helperText={
                                errors[campo.id]
                                  ? errors[campo.id]
                                  : campo.texto_ajuda
                              }
                              disabled={
                                currentFormsAnswer?.formulario ===
                                currentPageData.slug ||
                                !!Object.keys(valuesPattern).find(
                                  chave =>
                                    valuesPattern[chave] === values[campo.id],
                                )
                              }
                              numberFilesPermitted={campo?.limite_replicacoes}
                              tipos_arquivos={campo.tipos_arquivos}
                              tamanho_maximo_permitido={campo.tamanho_maximo_permitido}
                            />
                          </Grid>
                        )}
                      </Fragment>

                    )}
                  </Fragment>
                ))}
              </Grid>
            )}

            {/* Navegação padrão */}
            <Grid
              container
              direction='row'
              justify='space-between'
              alignItems='center'
              className={classes.gridContainer}
            >
              <Grid item xs={3}>
                {page > 0 && (
                  <Button
                    style={{ color: '#264259' }}
                    onClick={() => handlePrev()}
                  >
                    VOLTAR
                  </Button>
                )}
              </Grid>
              <Grid item xs={9} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                {flowPreviewEnabled && formData.length > 0 && page === formData.length - 1 && (
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={() => {
                      const allValuesFromCookie = readAllValuesFromCookie();
                      const allValuesCombined = { ...values, ...allValuesFromCookie };
                      setShowPreviewModal(true);
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    Visualizar
                  </Button>
                )}
                {hasPageNext() && (
                  <Button
                    className={classes.buttonForm}
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={
                      statusFormFlow?.pendentes?.length &&
                      page === formData.length - 1
                    }
                  >
                    {formData.length - 1 === page
                      ? 'SALVAR'
                      : 'SALVAR / AVANÇAR'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </>
      )}
      <PreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        formData={formData}
        allValues={(() => {
          const allPageValues: Record<string, any> = {};

          formData.forEach(form => {
            const pageValues = readPageValuesFromStorage(form.slug);
            if (pageValues) {
              Object.keys(pageValues).forEach(key => {
                allPageValues[String(key)] = pageValues[key];
                allPageValues[Number(key)] = pageValues[key];
              });
            }
          });

          const cookieValues = readAllValuesFromCookie() || {};
          const normalizedCookieValues: Record<string, any> = {};
          Object.keys(cookieValues).forEach(key => {
            normalizedCookieValues[String(key)] = cookieValues[key];
            normalizedCookieValues[Number(key)] = cookieValues[key];
          });

          const filesStorage = hasWindow() ? (() => {
            try {
              const stored = window.localStorage.getItem(getFilesStorageKey());
              return stored ? JSON.parse(stored) : {};
            } catch {
              return {};
            }
          })() : {};

          const normalizedValues: Record<string, any> = {};
          Object.keys(values).forEach(key => {
            normalizedValues[String(key)] = values[key];
            normalizedValues[Number(key)] = values[key];
          });

          const merged = { ...allPageValues, ...normalizedCookieValues, ...normalizedValues };

          Object.keys(filesStorage).forEach(key => {
            const currentValue = merged[String(key)] || merged[Number(key)];
            const fileValue = filesStorage[key];

            const hasFileObject = currentValue && typeof currentValue === 'object' &&
              (currentValue.url || currentValue.nome || currentValue.id);

            const isArrayOfFileObjects = Array.isArray(currentValue) && currentValue.length > 0 &&
              currentValue[0] && typeof currentValue[0] === 'object' &&
              (currentValue[0].url || currentValue[0].nome || currentValue[0].id);

            if (!currentValue ||
              currentValue === '' ||
              (Array.isArray(currentValue) && currentValue.length === 0) ||
              (!hasFileObject && !isArrayOfFileObjects && !(currentValue instanceof File) && !(currentValue instanceof FileList) && fileValue)) {
              merged[String(key)] = fileValue;
              merged[Number(key)] = fileValue;
            }
          });

          return merged;
        })()}
      />
    </>
  );
}